import { z } from "zod";
import { AccountType, AgreementId, AgreementSignatureStatus, AgreementSignatureTypes, AltAssetId, BeneficiaryTypes, ClientId, Custodian, CustodianAccountId, custodianOptions, EntityId, ExternalAccountId, FartherAccountId, FundingAccountStatusOptions, FundingAccountType, FundingMovementTypeOptions, LocalDate, OperationalState, PershingStandingInstructionFundingStatusOptions, PhysicalAccountId, PlanId, Prefix, Suffix, taxTypeOptions, USAStates, } from "../Types";
export const interestRateTypeOptions = [
    "FederalTargetLimit",
    "Prime",
    "Other",
    "InterestRateScheduleProvided",
];
const InterestRate = z.enum(interestRateTypeOptions);
export const trustTypeOptions = [
    "Charitable",
    "Family",
    "Generation Skipping",
    "Irrevocable",
    "Living",
    "Qualified Terminable Interest Property",
    "Revocable",
    "Statutory",
    "Testamentary",
    "Irrevocable Living",
    "Defective",
    "Insurance",
    "Special Needs",
    "Qualified Domestic",
];
export const Name = z.object({
    prefix: Prefix.nullable(),
    first: z.string(),
    middle: z.string().nullable(),
    last: z.string(),
    suffix: Suffix.nullable(),
});
export const Client = z.object({
    clientId: ClientId,
    name: Name,
});
export const ClientWithOwnership = Client.extend({
    ownershipPercentage: z.number(),
});
export const Beneficiary = z.object({
    beneficiaryId: ClientId,
    beneficiaryType: z.enum(BeneficiaryTypes),
    allocationPercentage: z.number().nullish(),
    name: Name,
});
export const AssetLendingData = z.object({
    interestRateType: InterestRate,
    interestRateTypeDescription: z.string().nullable(),
    collateralPhysicalAccountId: z.string().nullable(),
    custodianAccountNumber: CustodianAccountId.nullish(),
    purposeOfCredit: z.string().nullable(),
    interestOperator: z.boolean(),
    delivery: z.boolean(),
    baseRate: z.number(),
    spread: z.number(),
    loanAmount: z.number(),
});
export const PlanDocument = z.object({
    agreementId: AgreementId,
    clientId: ClientId,
    physicalAccountId: PhysicalAccountId.nullable(),
    title: z.string(),
    signatureType: z.enum(AgreementSignatureTypes),
    status: AgreementSignatureStatus,
});
const AccountPeople = z.object({
    trustees: ClientWithOwnership.array().optional(),
    beneficiaries: Beneficiary.array().optional(),
    accountHolder: ClientWithOwnership.nullish(),
    primaryAccountHolder: ClientWithOwnership.nullish(),
    secondaryAccountHolders: ClientWithOwnership.array().optional(),
});
export const TrustData = z.object({
    owner: Client,
    entity: z.object({
        entityId: EntityId,
        name: z.string(),
    }),
    grantor: Client.nullable(),
    revoker: Client.nullable(),
    trustName: z.string(),
    trustType: z.enum(trustTypeOptions),
    trustPurpose: z.string().nullish(),
    governingState: z.enum(USAStates),
    dateEstablished: z.string(),
    taxInformation: z.object({
        taxIdType: z.enum(taxTypeOptions),
        taxId: z.string(),
    }),
    options: z.object({
        ptCashManagement: z.boolean(),
        ptGeneralBorrowing: z.boolean(),
        ptMargin: z.boolean(),
        ptOptionTrading: z.boolean(),
        ptPowerOfAttorney: z.boolean(),
        trusteeAuthority: z.object({
            trusteesIndividualAuthority: z.boolean(),
            corporateTrusteesIndividualAuthority: z.boolean(),
        }),
    }),
});
export const InheritedIRAInformation = z.object({
    precedingAccountHolderId: z.string(),
    originalAccountHolderId: z.string(),
    isPrecedingAccountHolderSpouseOfOriginalAccountHolder: z.boolean(),
    isPrecedingAccountHolderOriginal: z.boolean(),
    isOriginalBeneficiarySpouseOfOriginalAccountHolder: z.boolean(),
    originalBeneficiaryDateOfBirth: LocalDate,
});
export const RetirementData = z.object({
    employerTin: z.string().nullable(),
    isRothAgreement: z.boolean().nullable(),
    taxIdType: z.enum(taxTypeOptions).nullable(),
    inheritedIRAInformation: InheritedIRAInformation.nullable(),
});
export const JointData = z.object({
    jointTenancyState: z.enum(USAStates),
    jointHoldersMarried: z.boolean(),
    agreementDate: z.string(),
});
export const AccountInformation = z.object({
    accountTitle: z.string(),
    accountType: AccountType,
    accountTarget: z.object({ goal: z.number(), date: z.string() }).nullable(),
    retirementData: RetirementData.nullish(),
    trustData: TrustData.nullish(),
    jointData: JointData.nullish(),
    assetLendingData: AssetLendingData.nullish(),
});
export const Account = z.object({
    virtualAccountId: FartherAccountId,
    physicalAccountId: PhysicalAccountId,
    custodian: z.enum(custodianOptions),
    custodianAccountNumber: CustodianAccountId.nullish(),
    accountInformation: AccountInformation,
    accountPeople: AccountPeople,
});
export const FundingAccountDetails = z.object({
    account: z.object({
        accountType: FundingAccountType,
        accountTitle: z.string(),
        accountNumber: z.string(),
        accountHolderNames: z.string().array(),
    }),
    bank: z.object({
        bankId: z.string(),
        routingNumber: z.string(),
        name: z.string(),
        city: z.string(),
        state: z.enum(USAStates),
    }),
});
const FundingResource = z.object({
    instructionId: z.string(),
    virtualAccountId: FartherAccountId,
    physicalAccountId: PhysicalAccountId,
    externalAccountId: ExternalAccountId,
    enterpriseId: z.string(),
    sequenceNumber: z.string(),
    status: z.enum(PershingStandingInstructionFundingStatusOptions),
    movementType: z.enum(FundingMovementTypeOptions),
});
const FundingAccount = z.object({
    accountId: ExternalAccountId,
    accountProvider: z.string(),
    clientId: ClientId,
    details: FundingAccountDetails,
    fundingDetails: z.object({
        fundingStatus: z.enum(FundingAccountStatusOptions),
        planId: PlanId,
        fundingResources: FundingResource.array(),
        validAchAccountIds: z.string().array(),
    }),
});
export const planStatusOptions = [
    "Not Started",
    "In Progress",
    "Ready to Review",
    "Complete",
];
export const PlanStatus = z.enum(planStatusOptions);
export const planTypeOptions = ["New Accounts", "Transfers"];
export const PlanType = z.enum(planTypeOptions);
const Plan = z.object({
    planId: PlanId,
    planTitle: z.string(),
    planStatus: PlanStatus,
    planType: PlanType,
    planHolder: Client,
});
export const PlanDetails = z.object({
    plan: Plan,
    resources: z.object({
        accounts: Account.array(),
        documents: PlanDocument.array(),
        fundingAccounts: FundingAccount.array(),
    }),
    isValidPlan: z.boolean(),
});
export const FartherManagedAccountDetails = z.object({
    accountType: AccountType,
    displayName: z.string(),
    operationalState: OperationalState,
    targetDate: z.string().nullable(),
    targetGoal: z.number().nullable(),
    custodian: Custodian,
    custodianAccountNumber: CustodianAccountId.nullable(),
    lastReportedBalance: z.object({
        balance: z.number().nullable(),
    }),
});
export const FartherManagedAccount = z.object({
    virtualAccountId: FartherAccountId,
    accountDetails: FartherManagedAccountDetails,
    accountOwner: ClientId.array(),
    sharedBy: ClientId.array(),
});
export const FartherManagedAccounts = FartherManagedAccount.array();
export const ManuallyTrackedAccount = z.object({
    id: AltAssetId,
    personId: ClientId,
    assetName: z.string(),
    assetType: z.enum([
        "Real Estate",
        "Private Investment Holding",
        "Crypto",
        "Collectable",
    ]),
    assetValue: z.number(),
    assetStatus: z.enum(["Active", "Sold"]),
    documentName: z.string().nullable(),
    createTime: z.string(),
    modifyTime: z.string(),
});
export const ManuallyTrackedAccounts = ManuallyTrackedAccount.array();
//# sourceMappingURL=Types.js.map